import React from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CancelSubscriptionDialog } from "../../components/CancelSubscriptionDialog";
import { DualColorProgressBar } from "@/components/common/DualColorProgressBar";
import { useBilling } from "../hooks/use-billing";
import { PaymentBrandLogo } from "../../components/PaymentBrandLogo";
import mixpanel from "mixpanel-browser";

export const BillingMethodSection: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Billing hook - handles all state and actions
  const {
    billingData,
    isBillingLoading,
    isPaymentMethodUpdateLoading,
    handleEditPaymentMethod,
    openCancelDialog,
    handleUpgrade,
  } = useBilling();

  // Helper functions for plan data
  const getPlanName = () => {
    return (
      billingData?.data.pricingPlan?.name || t("settings.billing.basicPlan")
    );
  };

  const getPlanPrice = () => {
    const plan = billingData?.data.pricingPlan;
    if (!plan) return "10";

    // Use discounted price if available, otherwise original price
    const price =
      plan.discountPercentage > 0 ? plan.discountedPrice : plan.originalPrice;
    return price.toString();
  };

  const getBillingCycle = () => {
    const plan = billingData?.data.pricingPlan;
    if (!plan) return t("settings.billing.monthly");

    return plan.billingCycle === "yearly"
      ? t("settings.billing.yearly")
      : t("settings.billing.monthly");
  };

  const getPlanDescription = () => {
    const plan = billingData?.data.pricingPlan;
    if (!plan) return t("settings.billing.mostPopular");

    if (plan.isPopular) return t("settings.billing.mostPopular");
    if (plan.isFeatured) return t("settings.billing.featuredPlan");
    return t("settings.billing.standardPlan");
  };

  const hasDiscount = () => {
    const plan = billingData?.data.pricingPlan;
    return plan && plan.discountPercentage > 0;
  };

  const getOriginalPrice = () => {
    const plan = billingData?.data.pricingPlan;
    return plan?.originalPrice?.toString() || "";
  };

  const subscription = billingData?.data.subscription;

  const formattedExpiry = subscription?.expiresAt
    ? new Date(subscription.expiresAt).toLocaleDateString(i18n.language, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  // "resets on" for active subs; a cancelled label for canceled subs
  const renderSubscriptionStatus = () => {
    if (!subscription || !formattedExpiry) return null;

    if (subscription.status === "canceled") {
      return (
        <p className="text-xs font-medium text-red-400 dark:text-red-300">
          {t("settings.billing.subscriptionCanceled", { date: formattedExpiry })}
        </p>
      );
    }

    if (subscription.status === "active") {
      return (
        <p className="text-xs text-text-tertiary dark:text-text-tertiary">
          {t("settings.billing.subscriptionResetsOn", { date: formattedExpiry })}
        </p>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-primary mb-2">
          {t("settings.billing.billingMethod")}
        </h3>
        <p className="text-sm text-text-tertiary dark:text-text-tertiary">
          {t("settings.billing.billingMethodDescription")}
        </p>
      </div>

      {/* Desktop: Side by side layout */}
      <div className="hidden lg:grid lg:grid-cols-2 lg:gap-6">
        {/* Left: Current Plan Card */}
        <div className="bg-white dark:bg-secondary border border-border-secondary dark:border-none rounded-xl p-6">
          <div className="flex flex-col gap-6">
            {/* Plan Header */}
            {isBillingLoading ? (
              <div className="flex justify-between items-start animate-pulse">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-16 h-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                  </div>
                  <div className="w-32 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div className="flex items-end gap-1">
                  <div className="w-12 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="w-16 h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="w-6 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="w-20 h-5 bg-gray-200 dark:bg-gray-700 rounded ml-1"></div>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-start">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-lg font-semibold text-text-primary dark:text-text-primary">
                      {getPlanName()}
                    </h4>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-text-secondary dark:text-text-secondary">
                      {getBillingCycle()}
                    </span>
                  </div>
                  <p className="text-sm text-text-tertiary dark:text-text-tertiary">
                    {getPlanDescription()}
                  </p>
                </div>
                <div className="flex items-end gap-1">
                  {hasDiscount() && (
                    <span className="text-lg font-medium text-text-tertiary dark:text-text-tertiary line-through mr-1">
                      {billingData?.data.pricingPlan?.currency}
                      {getOriginalPrice()}
                    </span>
                  )}
                  <span className="text-5xl font-semibold text-text-primary dark:text-text-primary">
                    {getPlanPrice()}
                  </span>
                  <span className="text-3xl font-semibold text-text-primary dark:text-text-primary">
                    {billingData?.data.pricingPlan?.currency}
                  </span>
                  <span className="text-lg font-medium text-text-tertiary dark:text-text-tertiary ml-1">
                    {t("settings.billing.perMonth")}
                  </span>
                </div>
              </div>
            )}

            {/* Subscription reset / cancelled status */}
            {renderSubscriptionStatus()}

            {/* Credits Progress */}
            <div className="flex flex-col gap-3">
              {isBillingLoading ? (
                <div className="space-y-3 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="w-32 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-8 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div className="bg-gray-300 dark:bg-gray-600 h-3 rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="w-20 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="w-20 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                      <div className="w-16 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
              ) : billingData?.data.usage ? (
                <DualColorProgressBar
                  subscriptionRemaining={
                    billingData.data.usage.tokens.subscription
                  }
                  subscriptionMax={
                    billingData.data.usage.maxTokens.subscription
                  }
                  oneTimeRemaining={billingData.data.usage.tokens.oneTime}
                  oneTimeMax={billingData.data.usage.maxTokens.oneTime}
                />
              ) : (
                <>
                  <p className="text-sm font-medium text-text-primary dark:text-text-primary">
                    {t("settings.billing.loadingUsage")}...
                  </p>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div className="bg-gray-300 dark:bg-gray-600 h-3 rounded-full animate-pulse"></div>
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pt-4 border-t border-border-secondary dark:border-border-secondary">
              {isBillingLoading ? (
                <div className="flex gap-4 animate-pulse">
                  <div className="w-20 h-9 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="w-28 h-9 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              ) : (
                <>
                  {/* Cancel button - sadece paid plan'larda göster */}
                  {billingData?.data.subscription?.status == "active" &&
                    billingData?.data.pricingPlan &&
                    billingData?.data.pricingPlan.originalPrice > 0 && (
                      <Button
                        variant="ghost"
                        onClick={openCancelDialog}
                        className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        {t("settings.billing.cancelPlan")}
                      </Button>
                    )}
                  <Button
                    onClick={() => {
                      mixpanel.track("upgrade_plan_clicked");
                      window.netmera.push((api) => {
                        api.sendEvent({
                          code: "qcsfp",
                        });
                      });
                      handleUpgrade();
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    {t("settings.billing.upgradePlan")}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right: Payment Method Card */}
        <div className="bg-white dark:bg-secondary border border-border-secondary dark:border-none rounded-xl p-6">
          <div className="flex flex-col gap-5">
            <div>
              <h4 className="text-base font-semibold text-text-primary dark:text-text-primary mb-1">
                {t("settings.billing.paymentMethod")}
              </h4>
              <p className="text-sm text-text-tertiary dark:text-text-tertiary">
                {t("settings.billing.paymentMethodDescription")}
              </p>
            </div>

            {/* Payment Card */}
            {isBillingLoading ? (
              <div className="bg-white dark:bg-secondary border border-border-secondary dark:border-none rounded-lg p-4">
                <div className="flex items-center justify-center py-8">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-text-tertiary dark:text-text-tertiary">
                      {t("common.loading")}...
                    </p>
                  </div>
                </div>
              </div>
            ) : billingData?.data.paymentMethod ? (
              <div className="bg-white dark:bg-secondary border border-border-secondary  rounded-lg p-4">
                <div className="flex items-start gap-4">
                  {/* Card Brand Logo */}
                  <PaymentBrandLogo
                    brand={billingData.data.paymentMethod.brand}
                    className="w-14 h-10"
                  />

                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-sm font-medium text-text-secondary dark:text-text-secondary">
                          {t("settings.billing.visaEnding")}{" "}
                          {billingData.data.paymentMethod.last4}
                        </p>
                        <p className="text-sm text-text-tertiary dark:text-text-tertiary">
                          {t("settings.billing.expiry")}{" "}
                          {billingData.data.paymentMethod.exp_month
                            .toString()
                            .padStart(2, "0")}
                          /{billingData.data.paymentMethod.exp_year}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditPaymentMethod}
                        disabled={isPaymentMethodUpdateLoading}
                        className="bg-green-500 hover:bg-green-600 text-white disabled:opacity-50"
                      >
                        {isPaymentMethodUpdateLoading
                          ? t("common.loading")
                          : t("settings.billing.edit")}
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 text-text-tertiary dark:text-text-tertiary" />
                      <span className="text-sm text-text-tertiary dark:text-text-tertiary">
                        {billingData.data.paymentMethod.email}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-bg-secondary border border-border-secondary dark:border-none rounded-lg p-4">
                <div className="flex flex-col items-center justify-center py-8 gap-4">
                  <p className="text-sm text-text-tertiary dark:text-text-tertiary">
                    {t("settings.billing.noPaymentMethod")}
                  </p>
                  <Button
                    onClick={handleEditPaymentMethod}
                    disabled={isPaymentMethodUpdateLoading}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-[10px] disabled:opacity-50"
                  >
                    {isPaymentMethodUpdateLoading
                      ? t("common.loading")
                      : t("settings.paymentMethod.addButton")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile: Stacked layout */}
      <div className="flex flex-col gap-4 lg:hidden">
        {/* Current Plan Card */}
        <div className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-border-secondary rounded-xl p-4">
          <div className="flex flex-col gap-4">
            {/* Plan Header */}
            {isBillingLoading ? (
              <div className="flex flex-col gap-3 animate-pulse">
                {/* Plan Info Skeleton */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                  </div>
                  <div className="w-28 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>

                {/* Price Skeleton */}
                <div className="flex items-center gap-1">
                  <div className="w-12 h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="w-4 h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded ml-1"></div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {/* Plan Info Section */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-semibold text-text-primary dark:text-text-primary">
                      {getPlanName()}
                    </h4>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-text-secondary dark:text-text-secondary">
                      {getBillingCycle()}
                    </span>
                  </div>
                  <p className="text-xs text-text-tertiary dark:text-text-tertiary">
                    {getPlanDescription()}
                  </p>
                </div>

                {/* Price Section */}
                <div className="flex items-center gap-1">
                  {hasDiscount() && (
                    <span className="text-sm font-medium text-text-tertiary dark:text-text-tertiary line-through mr-2">
                      {billingData?.data.pricingPlan?.currency}
                      {getOriginalPrice()}
                    </span>
                  )}
                  <span className="text-2xl font-semibold text-text-primary dark:text-text-primary">
                    {billingData?.data.pricingPlan?.currency}
                  </span>
                  <span className="text-3xl font-semibold text-text-primary dark:text-text-primary">
                    {getPlanPrice()}
                  </span>
                  <span className="text-sm font-medium text-text-tertiary dark:text-text-tertiary ml-1">
                    {t("settings.billing.perMonth")}
                  </span>
                </div>
              </div>
            )}

            {/* Subscription reset / cancelled status */}
            {renderSubscriptionStatus()}

            {/* Credits Progress */}
            <div className="flex flex-col gap-2">
              {isBillingLoading ? (
                <div className="space-y-2 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="w-28 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-6 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-gray-300 dark:bg-gray-600 h-2 rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex flex-col gap-1 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                      <div className="w-12 h-2 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
              ) : billingData?.data.usage ? (
                <DualColorProgressBar
                  subscriptionRemaining={
                    billingData.data.usage.tokens.subscription
                  }
                  subscriptionMax={
                    billingData.data.usage.maxTokens.subscription
                  }
                  oneTimeRemaining={billingData.data.usage.tokens.oneTime}
                  oneTimeMax={billingData.data.usage.maxTokens.oneTime}
                />
              ) : (
                <>
                  <p className="text-xs font-medium text-text-primary dark:text-text-primary">
                    {t("settings.billing.loadingUsage")}...
                  </p>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-gray-300 dark:bg-gray-600 h-2 rounded-full animate-pulse"></div>
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 pt-3 border-t border-border-secondary dark:border-border-secondary">
              {isBillingLoading ? (
                <div className="flex flex-col gap-2 animate-pulse">
                  <div className="w-full h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="w-full h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              ) : (
                <>
                  {/* Cancel button - sadece paid plan'larda göster */}
                  {billingData?.data.subscription?.status == "active" &&
                    billingData?.data.pricingPlan &&
                    billingData.data.pricingPlan.originalPrice > 0 && (
                      <Button
                        variant="ghost"
                        onClick={openCancelDialog}
                        className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        {t("settings.billing.cancelPlan")}
                      </Button>
                    )}
                  <Button
                    onClick={() => {
                      mixpanel.track("upgrade_plan_clicked");
                      window.netmera.push((api) => {
                        api.sendEvent({
                          code: "qcsfp",
                        });
                      });
                      handleUpgrade();
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    {t("settings.billing.upgradePlan")}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Payment Method Card */}
        <div className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-border-secondary rounded-xl p-4">
          <div className="flex flex-col gap-4">
            <div>
              <h4 className="text-sm font-semibold text-text-primary dark:text-text-primary mb-1">
                {t("settings.billing.paymentMethod")}
              </h4>
              <p className="text-xs text-text-tertiary dark:text-text-tertiary">
                {t("settings.billing.paymentMethodDescription")}
              </p>
            </div>

            {/* Payment Card */}
            {isBillingLoading ? (
              <div className="bg-white dark:bg-gray-700 border border-border-secondary dark:border-border-secondary rounded-lg p-3">
                <div className="flex items-center justify-center py-6">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-xs text-text-tertiary dark:text-text-tertiary">
                      {t("common.loading")}...
                    </p>
                  </div>
                </div>
              </div>
            ) : billingData?.data.paymentMethod ? (
              <div className="bg-white dark:bg-gray-700 border border-border-secondary dark:border-border-secondary rounded-lg p-3">
                <div className="flex items-start gap-3">
                  {/* Card Brand Logo */}
                  <PaymentBrandLogo
                    brand={billingData.data.paymentMethod.brand}
                    className="w-12 h-8"
                  />

                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <p className="text-xs font-medium text-text-secondary dark:text-text-secondary">
                          {t("settings.billing.visaEnding")}{" "}
                          {billingData.data.paymentMethod.last4}
                        </p>
                        <p className="text-xs text-text-tertiary dark:text-text-tertiary">
                          {t("settings.billing.expiry")}{" "}
                          {billingData.data.paymentMethod.exp_month
                            .toString()
                            .padStart(2, "0")}
                          /{billingData.data.paymentMethod.exp_year}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditPaymentMethod}
                        disabled={isPaymentMethodUpdateLoading}
                        className="h-7 px-2 text-xs disabled:opacity-50"
                      >
                        {isPaymentMethodUpdateLoading
                          ? t("common.loading")
                          : t("settings.billing.edit")}
                      </Button>
                    </div>
                    <div className="flex items-center gap-1">
                      <Mail className="w-3 h-3 text-text-tertiary dark:text-text-tertiary" />
                      <span className="text-xs text-text-tertiary dark:text-text-tertiary">
                        {billingData.data.paymentMethod.email}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-700 border border-border-secondary dark:border-border-secondary rounded-lg p-3">
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <p className="text-xs text-text-tertiary dark:text-text-tertiary">
                    {t("settings.billing.noPaymentMethod")}
                  </p>
                  <Button
                    onClick={handleEditPaymentMethod}
                    disabled={isPaymentMethodUpdateLoading}
                    className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-[10px] text-xs h-8 disabled:opacity-50"
                  >
                    {isPaymentMethodUpdateLoading
                      ? t("common.loading")
                      : t("settings.paymentMethod.addButton")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Subscription Dialog - Zero Props */}
      <CancelSubscriptionDialog />
    </div>
  );
};
